import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Select,
  MenuItem,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";

const glass = {
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  borderRadius: "18px",
};

const STATUS_OPTIONS = ["New", "Contacted", "Converted", "Closed"];

const statusColor = {
  New: { bg: "#e3f2fd", color: "#1565c0" },
  Contacted: { bg: "#fff3e0", color: "#ef6c00" },
  Converted: { bg: "#e8f5e9", color: "#2e7d32" },
  Closed: { bg: "#eceff1", color: "#546e7a" },
};

export default function Enquiries() {
  const token = localStorage.getItem("token");
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteGuid, setDeleteGuid] = useState(null);

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(apiEndpoints.enquiries, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      showSnackbar("Failed to load enquiries", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = async (guid, status) => {
    try {
      await axios.put(
        `${apiEndpoints.enquiries}?guid=${guid}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) =>
        prev.map((r) => (r.guid === guid ? { ...r, status } : r)),
      );
      showSnackbar("Status updated", "success");
    } catch (err) {
      showSnackbar("Failed to update status", "error");
    }
  };

  const handleDeleteClick = (guid) => {
    setDeleteGuid(guid);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${apiEndpoints.enquiries}?guid=${deleteGuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.guid !== deleteGuid));
      showSnackbar("Enquiry deleted", "success");
    } catch (err) {
      showSnackbar("Failed to delete enquiry", "error");
    } finally {
      setDeleteOpen(false);
      setDeleteGuid(null);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const matchesStatus =
        statusFilter === "all" || r.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        r.parent_name?.toLowerCase().includes(q) ||
        r.mobile?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.child_name?.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [rows, statusFilter, search]);

  return (
    <Box sx={{ p: 3, ...glass }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        mb={3}
        sx={{
          flexWrap: "wrap",
          justifyContent: isMobile ? "center" : "flex-start",
        }}
      >
        <Avatar sx={{ background: "#ffffffaa" }}>
          <ContactMailIcon sx={{ color: "#3a3d43ff" }} />
        </Avatar>
        <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 700, color: "#3a3d43ff" }}>
          Admission Enquiries
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={3}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <TextField
          size="small"
          placeholder="Search by name, mobile, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { sm: 280 }, background: "#fff", borderRadius: 1 }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160, background: "#fff", borderRadius: 1 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Paper sx={{ ...glass, p: 0, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Parent Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Child Name</TableCell>
                <TableCell>Interested Class</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Received On</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.guid} hover>
                  <TableCell>{row.parent_name}</TableCell>
                  <TableCell>{row.mobile}</TableCell>
                  <TableCell>{row.email || "-"}</TableCell>
                  <TableCell>{row.child_name || "-"}</TableCell>
                  <TableCell>{row.interested_class || "-"}</TableCell>
                  <TableCell>{row.branch_name || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: "normal" }}>
                    {row.message || "-"}
                  </TableCell>
                  <TableCell>
                    {row.createdOn
                      ? new Date(row.createdOn).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={row.status}
                      onChange={(e) =>
                        handleStatusChange(row.guid, e.target.value)
                      }
                      sx={{
                        minWidth: 130,
                        fontSize: 13,
                        "& .MuiSelect-select": {
                          py: 0.5,
                          background: statusColor[row.status]?.bg,
                          color: statusColor[row.status]?.color,
                          fontWeight: 600,
                          borderRadius: 1,
                        },
                      }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(row.guid)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No enquiries found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DeleteConfirmDialog
        open={deleteOpen}
        title="Delete Enquiry"
        message="Are you sure you want to delete this enquiry?"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
